import { useState, type FormEvent, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { authClient, useSession } from "@/frontend/auth-client";
import { orpc } from "@/frontend/orpc-client";
import { SplitGridLayout } from "./SplitGridLayout";
import { NameStep } from "./NameStep";

/**
 * Renders the "What's your name?" screen for a signed-in org member whose
 * profile has no name, which usually means a pre-provisioned user on first
 * sign-in. Saving through better-auth `updateUser` refreshes `useSession`, so
 * the gate falls through once a name is set.
 */
export function CompleteProfileGate({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  // The invite-accept page collects the invitee's name itself, so intercepting
  // it leaves a nameless invitee unable to accept the invitation at all.
  const isInviteAcceptPage = window.location.pathname === "/invite/accept";
  // `/delete-your-data` is a public patient page with its own portal sign-in.
  // A signed-in org member landing on it must reach the page, not a name prompt.
  const isDeletePatientDataPage = window.location.pathname === "/delete-your-data";
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const actorContextQuery = useQuery({
    ...orpc.org.getActorContext.queryOptions({ input: {} }),
    enabled: !!session,
  });

  const updateNameMutation = useMutation({
    mutationFn: async (newName: string) => {
      const result = await authClient.updateUser({ name: newName });
      if (result.error) throw new Error(result.error.message || "Failed to save name");
    },
    onError: (err: Error) => setError(err.message),
  });

  const needsName =
    !!session &&
    !isInviteAcceptPage &&
    !isDeletePatientDataPage &&
    actorContextQuery.data?.actorType === "orgMember" &&
    !session.user.name?.trim();

  if (!needsName) return <>{children}</>;

  /** Validates and saves the entered name. */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    updateNameMutation.mutate(name.trim());
  };

  return (
    <SplitGridLayout contentClassName="max-w-sm">
      <NameStep
        value={name}
        onChange={setName}
        onSubmit={handleSubmit}
        submitting={updateNameMutation.isPending}
        error={error}
      />
    </SplitGridLayout>
  );
}
